import { useEffect, useState, useContext } from "react";
// my imports:
import HintManager, { HintManagerEntry } from "@/app/components/HintManager";
import UI from "@/app/lib/ui/ui";
import WordModule from "../lib/game-state/word-module";
import LetterModule, { LMType } from "../lib/game-state/letter-module";
import { Contexts } from "@/app/components/Context";
import { AnyGameObj, AnyGameState } from "@/app/lib/game-state/game-types";
// style:
import hintStyles from "@/app/styles/hint.module.css";
import { InternalRouter } from "@/app/lib/routing/internal-router";
import { GameManager } from "@/app/lib/game-state/game-manager";

export type TutorialProps = {
    gameLoaded: boolean;
}

export type TutorialEntry = {
    position: {word: number, letter: number} | {ingredient: number} | {special: 'given' | 'entry'}
    text: string,
}

const tutorialEntries: TutorialEntry[] = [
    {
        position: {word: 1, letter: 2},
        text: "Welcome to Alphabet Soup. Let's learn how to play!"
    },
    {
        position: {word: 2, letter: 1},
        text: 'These rows of boxes are "recipes." Each recipe is a partially filled-in word.'
    },
    {
        position: {special: 'given'},
        text: "Some of the boxes, like this one, are filled with green letters. They can't change."
    },
    {
        position: {special: 'entry'},
        text: "Other boxes, like this one, start empty. They're up to you to fill in. But..."
    },
    {
        position: {ingredient: 1},
        text: 'You can only use the letters down here. These are the "ingredients."'
    },
    {
        position: {ingredient: 2},
        text: "You can use each ingredient as many times as you'd like, and you don't have to use them all."
    },
    {
        position: {word: 3, letter: 2},
        text: "Puzzles are generated on demand, so you'll never run out! Let's play!"
    }
]

export default function Tutorial({ gameLoaded } : TutorialProps) {

    const [managerEntries, setManagerEntries] = useState<HintManagerEntry[]>([]);
    const [finished, setFinished] = useState<boolean>(false);

    const gameManager: GameManager | null = useContext(Contexts.GameManager);
    const activeGame: AnyGameObj | null = useContext(Contexts.ActiveGame);
    const ui: UI | null = useContext(Contexts.UI);

    if (!gameManager || !activeGame || !ui) {
        console.warn('bad context in Tutorial')
        return;
    }

    const iRouter = new InternalRouter(gameManager, ui);

    // This is a candidate for a refactor, doens't need to be an effect, but it's working so meh
    // builds the hint props by using element references to find positions
    useEffect(() => {

        if (!gameLoaded)
            return;

        if (tutorialEntries.length < 1)
            return;

        // check if we're all done
        if (finished) {
            iRouter.goPath('/');
            // reset finished so we can request another tutorial  later
            setFinished(false);
            return;
        }

        if (ui.path !== '/tutorial') {
            return;
        }

        // remove focus from the game
        ui.defocusAll();

        // we're going to build a temp array to later push using the state setter
        const tempManagerEntries: HintManagerEntry[] = [];
        for (const tutorialEntry of tutorialEntries) {
            // we grab a letter ref element if that's the format of the entry
            let element: HTMLElement | null = getElementRef(
                tutorialEntry,
                activeGame.state,
                ui
            )
            if (!element)
                    continue;

            tempManagerEntries.push({
                element: element,
                text: tutorialEntry.text,
            })
        }

        // after iterating through the hints, update with useState
        setManagerEntries(tempManagerEntries);

    }, [gameLoaded, finished, ui.path])

    if (managerEntries.length < 1)
        return null;

    if (ui.path !== '/tutorial') {
        return null;
    }

    return(
        <>
        <div className={hintStyles.greyOverlay}/>

        <HintManager 
            allEntries={managerEntries}
            useSkip={true}
            skipOnStartOnly={true}
            setFinished={setFinished}
        />
        </>
    )
}

function getElementRef(
    tutorialEntry: TutorialEntry,
    gameState: AnyGameState,
    ui: UI
) : HTMLElement | null {
    // we grab a letter ref element if that's the format of the entry
    let element: HTMLElement | null = null;
    // if our position is defined as by a recipe / wordbox
    if ("word" in tutorialEntry.position) {
        element = ui.letterRefs.current[tutorialEntry.position.word][tutorialEntry.position.letter]
    }
    // if our position is defined by an ingredient
    else if ("ingredient" in tutorialEntry.position) {
        element = ui.ingredientRefs.current[tutorialEntry.position.ingredient];
    }
    // if we use special rules to find a particular kind of letterbox
    else if ("special" in tutorialEntry.position) {
        let type: LMType = tutorialEntry.position.special === 'given' ?
            'given' : 
            'entry';

        const position = findGoodLetterBox(
            type,
            1,
            3,
            gameState
        )
        element = ui.letterRefs.current[position.wordIndex][position.letterPosition]
    }

    return element;
}

function findGoodLetterBox(
    type: LMType,
    minDesiredLetterPosition: number,
    maxDesiredLetterPosition: number,
    gameState: AnyGameState
) : {wordIndex: number, letterPosition: number} {

    let returnPosition = {wordIndex: 0, letterPosition: 0}

    // decrement from the last word
    for (let i = gameState.words.length - 1; i >= 0; i--) {
        const wordModule: WordModule = gameState.words[i];
        for (const letterModule of wordModule.letters) {
            if (letterModule.position < minDesiredLetterPosition) {
                continue;
            }
            if (letterModule.position > maxDesiredLetterPosition) {
                break;
            }
            if (letterModule.type === type) {
                return {
                    wordIndex: wordModule.index,
                    letterPosition: letterModule.position
                }
            }
        }
    }

    // if we made it down here, we didn't get one in the position we wanted, so let's go again, finding the leftmost on board
    // we iterate by letterposition, checking all words (starting at longest) in that position first, then moving to next position
    const longestWord: WordModule = gameState.words[gameState.words.length - 1]
    for (
        let currentPositionBeingChecked = maxDesiredLetterPosition + 1; // start looking after the positions we already looked
        currentPositionBeingChecked < longestWord.length - 1; // only check up to the last letter of the longest word
        currentPositionBeingChecked++
    ) {
        for (let i = gameState.words.length - 1; i >= 0; i--) {
            const wordModule: WordModule = gameState.words[i];
            // if word is too short, continue
            if (wordModule.length - 1 < currentPositionBeingChecked) {
                continue;
            }
            const letterModule: LetterModule = wordModule.letters[currentPositionBeingChecked];
            if (letterModule.type === type) {
                return {
                    wordIndex: wordModule.index,
                    letterPosition: letterModule.position
                }
            }
        }
    }

    return returnPosition;
}